// Types for GET /user-measurement-logs/user/{user}/filter

import { AppFile, BiaFitUser, LaravelPaginatedResponse } from "./user";

export const MEASUREMENT_TYPES = [
  "l_biceps_relaxed",
  "r_biceps_relaxed",
  "l_triceps_relaxed",
  "r_triceps_relaxed",
  "waist",
  "hip",
  "l_mid_thigh",
  "r_mid_thigh",
  "l_calf",
  "r_calf",
  "weight",
  "body_fat_pct",
] as const;

export type MeasurementType = (typeof MEASUREMENT_TYPES)[number];

// Wording and order mirror the mobile history screen
// (MEASUREMENT_LABELS / MEASUREMENT_FIELDS in body-measurements.types.ts).
export const MEASUREMENT_TYPE_LABELS: Record<MeasurementType, string> = {
  weight: "Weight",
  body_fat_pct: "Body Fat",
  l_biceps_relaxed: "L Biceps Relaxed",
  r_biceps_relaxed: "R Biceps Relaxed",
  l_triceps_relaxed: "L Triceps Relaxed",
  r_triceps_relaxed: "R Triceps Relaxed",
  waist: "Waist",
  hip: "Hip",
  l_mid_thigh: "L Mid Thigh",
  r_mid_thigh: "R Mid Thigh",
  l_calf: "L Calf",
  r_calf: "R Calf",
};

// Display order within a date card: weight, then body fat, then body fields.
export const MEASUREMENT_DISPLAY_ORDER: MeasurementType[] = [
  "weight",
  "body_fat_pct",
  "l_biceps_relaxed",
  "r_biceps_relaxed",
  "l_triceps_relaxed",
  "r_triceps_relaxed",
  "waist",
  "hip",
  "l_mid_thigh",
  "r_mid_thigh",
  "l_calf",
  "r_calf",
];

// The API returns no units - these are display-only.
export const MEASUREMENT_TYPE_UNITS: Record<MeasurementType, string> = {
  l_biceps_relaxed: "cm",
  r_biceps_relaxed: "cm",
  l_triceps_relaxed: "cm",
  r_triceps_relaxed: "cm",
  waist: "cm",
  hip: "cm",
  l_mid_thigh: "cm",
  r_mid_thigh: "cm",
  l_calf: "cm",
  r_calf: "cm",
  weight: "kg",
  body_fat_pct: "%",
};

// Structural, so types/ need not import from components/.
export const MEASUREMENT_TYPE_OPTIONS: { value: string; label: string }[] =
  MEASUREMENT_TYPES.map((type) => ({
    value: type,
    label: MEASUREMENT_TYPE_LABELS[type],
  }));

// A file of type 'user_measurement_image'
export type MeasurementAttachment = AppFile;

export interface UserMeasurementLog {
  id: number;
  user_id: number;
  measurement_type: MeasurementType;
  value: string; // decimal:2 cast - serialized as a string, e.g. "81.00"
  notes: string | null;
  measured_at: string | null;
  created_at: string;
  updated_at: string;
  user?: BiaFitUser;
  attachments?: MeasurementAttachment[];
}

// No user_id: the API prohibits it, the path carries the user.
export interface MeasurementLogFilters {
  measured_at?: string; // YYYY-MM-DD, exact day match
  measurement_type?: MeasurementType;
}

export interface UserMeasurementLogsApiResponse {
  data: LaravelPaginatedResponse<UserMeasurementLog>;
}

// GET /user-measurement-logs/user/{user}/grouped - paginated by DATE, not by
// row, and NOT double-wrapped (the backend skips the { data: ... } envelope
// here). Placeholder value=0 rows are already excluded server-side.
export interface GroupedMeasurementsMeta {
  current_page: number;
  per_page: number;
  total_dates: number;
  total_pages: number;
  total_measurements: number;
}

export interface GroupedMeasurementsApiResponse {
  // Keyed by YYYY-MM-DD, newest first. PHP serializes the empty set as []
  // instead of {}, hence the array alternative.
  data: Record<string, UserMeasurementLog[]> | UserMeasurementLog[];
  meta: GroupedMeasurementsMeta;
}
